import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";
import { InstallState } from "../../../../serviceapi/jobRunnerApi";
import { usePythonEnvironments } from "./usePythonEnvironments";

interface PythonEnvironmentManagerDialogProps {
  close: () => void;
}

const PythonEnvironmentManagerDialog = ({
  close,
}: PythonEnvironmentManagerDialogProps) => {
  const {
    environments,
    isLoading,
    isInstalling,
    installStatus,
    fetchEnvironments,
    installEnvironment,
    deleteEnvironment,
    resetInstallStatus,
  } = usePythonEnvironments();

  const [file, setFile] = useState<File | null>(null);
  const [envId, setEnvId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const isSucceeded = installStatus?.state === InstallState.Succeeded;
  const isFailed = installStatus?.state === InstallState.Failed;
  const isTerminal = isSucceeded || isFailed;

  const resetForm = () => {
    setFile(null);
    setEnvId("");
    setError("");
    resetInstallStatus();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleInstall = async () => {
    if (!file) {
      setError("Please select a file to install.");
      return;
    }
    setError("");
    await installEnvironment(file, envId);
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message: `Are you sure you want to delete environment ${id}?`,
      header: "Delete Environment",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => deleteEnvironment(id),
    });
  };

  const actionBodyTemplate = (id: string) => (
    <div className="flex justify-content-end">
      <Button
        className="p-button-text p-button-danger p-button-sm"
        icon="pi pi-trash"
        tooltip="Delete"
        onClick={() => handleDelete(id)}
        disabled={isInstalling}
      />
    </div>
  );

  const renderInstallSection = () => {
    if (isInstalling) {
      return (
        <div className="flex align-items-center gap-3 p-2">
          <ProgressSpinner
            style={{ width: "28px", height: "28px" }}
            strokeWidth="4"
          />
          <span>Installing…</span>
        </div>
      );
    }

    if (isTerminal) {
      return (
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-2">
            <i
              className={
                isSucceeded
                  ? "pi pi-check-circle"
                  : "pi pi-times-circle"
              }
              style={{
                fontSize: "1.5rem",
                color: isSucceeded ? "#22c55e" : "#ef4444",
              }}
            />
            <span>
              {isSucceeded
                ? `Environment "${installStatus?.environmentId}" installed.`
                : installStatus?.error || "Installation failed."}
            </span>
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              label="Logs"
              icon="pi pi-book"
              className="p-button-text"
              onClick={() => setShowLogs(true)}
            />
            <Button
              label="New install"
              icon="pi pi-plus"
              onClick={resetForm}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="p-fluid flex flex-column gap-3">
        <div className="p-field flex flex-column gap-2">
          <label htmlFor="envFile">Environment file *</label>
          <input
            id="envFile"
            ref={fileInputRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <div className="p-field flex flex-column gap-2">
          <label htmlFor="envId">Environment id (optional)</label>
          <InputText
            id="envId"
            value={envId}
            placeholder="Leave blank to create a new environment"
            onChange={(e) => setEnvId(e.target.value)}
          />
        </div>
        {error && <div style={{ color: "red" }}>{error}</div>}
        <Button
          label="Install"
          icon="pi pi-upload"
          onClick={handleInstall}
          disabled={!file}
        />
      </div>
    );
  };

  const renderLogSection = (title: string, content?: string | null) => {
    if (!content) return null;
    return (
      <div className="flex flex-column gap-1">
        <span className="font-medium">{title}</span>
        <pre
          style={{
            margin: 0,
            padding: "8px",
            maxHeight: "40vh",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
          }}
        >
          {content}
        </pre>
      </div>
    );
  };

  const hasAnyLog =
    !!installStatus?.stdout ||
    !!installStatus?.stderr ||
    !!installStatus?.error;

  return (
    <Dialog
      header="Python Environment Manager"
      visible
      onHide={close}
      style={{ width: "550px" }}
      modal
    >
      <div className="flex flex-column gap-4">
        <div className="flex flex-column gap-2">
          <div className="flex justify-content-between align-items-center">
            <label className="font-medium">Installed environments</label>
            <Button
              className="p-button-text p-button-sm"
              icon="pi pi-refresh"
              tooltip="Refresh"
              onClick={fetchEnvironments}
              disabled={isLoading}
            />
          </div>
          {isLoading ? (
            <div className="flex justify-content-center p-3">
              <ProgressSpinner
                style={{ width: "40px", height: "40px" }}
                strokeWidth="4"
              />
            </div>
          ) : (
            <DataTable
              value={environments.map((id) => ({ id }))}
              emptyMessage="No environments installed."
              scrollable
              scrollHeight="240px"
              dataKey="id"
            >
              <Column field="id" header="Environment" />
              <Column
                body={(row) => actionBodyTemplate(row.id)}
                style={{ width: "4rem" }}
              />
            </DataTable>
          )}
        </div>

        <div className="flex align-items-center gap-2">
          <div className="w-full border-top-1 border-gray-300"></div>
          <span className="text-gray-300 text-sm">INSTALL</span>
          <div className="w-full border-top-1 border-gray-300"></div>
        </div>

        {renderInstallSection()}
      </div>

      <Dialog
        header="Install Logs"
        visible={showLogs}
        onHide={() => setShowLogs(false)}
        style={{ width: "700px" }}
        modal
      >
        <div className="flex flex-column gap-3">
          {!hasAnyLog && <span>No logs available.</span>}
          {renderLogSection("Error", installStatus?.error)}
          {renderLogSection("Stdout", installStatus?.stdout)}
          {renderLogSection("Stderr", installStatus?.stderr)}
        </div>
      </Dialog>
    </Dialog>
  );
};

export default PythonEnvironmentManagerDialog;
